import { CustomerAccountState, IVerifyEmailResponse, SignInPanels } from "./IVerifyEmailResponse";

export class SignInBase {
    private continueElement = document.getElementById("continue");
    private emailElement = document.getElementById("Email") as HTMLInputElement;
    private readonly formSelector = "#sign-in-form";
    private backToEmailPanelElement = document.getElementById("back-to-email-panel-link");
    private localeUrlPrefix: string;
    private passwordElement = document.getElementById("Password") as HTMLInputElement;
    private setPasswordElement = document.getElementById("SetPassword") as HTMLInputElement;
    private validationCodeElement = document.getElementById("ValidationCode") as HTMLInputElement;

    constructor(private context: string, private newAccountUrl: string, private verifyLoginUrl: string, private setPasswordUrl: string) {
        this.backToEmailPanelElement.onclick = (ev) => this.onBackToEmailPanelClicked(ev);
        this.continueElement.onclick = async (ev) => await this.onContinueClickedAsync(ev);
        this.localeUrlPrefix = document.body.dataset.localeUrlPrefix;

        this.registerEnterKeyCallback(this.emailElement, () => { this.verifyEmail(); })
        this.registerEnterKeyCallback(this.passwordElement, async () => { await this.verifyLoginAsync(); })
        this.registerEnterKeyCallback(this.setPasswordElement, () => { this.setPassword(); })
        this.registerEnterKeyCallback(this.validationCodeElement, () => { this.validateAccount(); })
    }

    private verifyEmail(): void {

        if (!window.validationService.isFieldValid(this.emailElement)) {
            return;
        }

        window.FormSubmitToggle.DisableForm(this.formSelector);
        fetch(this.localeUrlPrefix + "/ws/verify-email", {
            body: JSON.stringify({ email: this.emailElement.value }),
            headers: { 'Content-Type': 'application/json' },
            method: "post",
        }).then((response: Response) => {
            if (response.ok) {
                return response.json();
            }
            window.IssueLogger.log("Unable to validate email address.", this.context);
            this.showValidationError("Unable to verify your email address.", SignInPanels.Email);
            window.FormSubmitToggle.EnableForm(this.formSelector);
        }).then((data: IVerifyEmailResponse) => {
            if (!data) { // Server or transport error.
                window.IssueLogger.log("Server error validating email address.", this.context);
                this.showValidationError("Unable to check your email address.", SignInPanels.Email);
                window.FormSubmitToggle.EnableForm(this.formSelector);
                return;
            }

            if (data.customerAccountState === CustomerAccountState.Interactive) {
                let passwordLabel = <HTMLLabelElement>document.getElementById("password-label");

                passwordLabel.innerText = "Please enter your password for " + this.emailElement.value + ":";
                this.setPanelVisibility(SignInPanels.Password);
                window.FormSubmitToggle.EnableForm(this.formSelector);
            }
            else if (data.customerAccountState === CustomerAccountState.DoesNotExist) {
                // No account by that email, move on to account create.
                window.location.href = this.newAccountUrl;
            }
            else if (data.customerAccountState === CustomerAccountState.NotInteractive) {
                this.setPanelVisibility(SignInPanels.Validation);
                window.FormSubmitToggle.EnableForm(this.formSelector);
            }
            else if (data.customerAccountState === CustomerAccountState.AccountDisabled) {
                this.setPanelVisibility(SignInPanels.Email);
                this.showValidationError("This account has been locked, please contact customer services for more information.", SignInPanels.Email);
                window.FormSubmitToggle.EnableForm(this.formSelector);
            }
            else {
                window.FormSubmitToggle.EnableForm(this.formSelector);
                this.setPanelVisibility(SignInPanels.Email);
                this.showValidationError("We could not log you in, if this problem persists please contact customer services.", SignInPanels.Email);
            }
        });
    }

    private showValidationError(errorText: string, panel: SignInPanels): void {
        this.setPanelError(errorText, SignInPanels.Email, panel === SignInPanels.Email);
        this.setPanelError(errorText, SignInPanels.Password, panel === SignInPanels.Password);
        this.setPanelError(errorText, SignInPanels.SetPassword, panel === SignInPanels.SetPassword);
        this.setPanelError(errorText, SignInPanels.Validation, panel === SignInPanels.Validation);
    }

    private setPanelError(text: string, panel: SignInPanels, isPanelActive: boolean): void {
        let elementId = "email-error-message";

        if (panel === SignInPanels.Password) {
            elementId = "password-error-message";
        }
        else if (panel === SignInPanels.SetPassword) {
            elementId = "set-password-error-message";
        }
        else if (panel === SignInPanels.Validation) {
            elementId = "validation-error-message";
        }

        let errorSpan = document.getElementById(elementId);
        errorSpan.innerText = text;

        if (isPanelActive) {
            errorSpan.classList.remove("field-validation-valid");
            errorSpan.classList.add("field-validation-error");
        }
        else {
            errorSpan.classList.remove("field-validation-error");
            errorSpan.classList.add("field-validation-valid");
        }
    }

    private onBackToEmailPanelClicked(ev): void {
        ev.preventDefault();
        this.setPanelVisibility(SignInPanels.Email);
    }

    // TODO: Standardise. This is based on the jQuery version and is the best one to use throughout the site.
    private isVisible(el: HTMLElement): boolean {
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    private async onContinueClickedAsync(ev): Promise<void> {
        ev.preventDefault();

        if (this.isVisible(this.emailElement)) {
            if (window.validationService.isFieldValid(this.emailElement)) {
                this.verifyEmail();
            }
        }
        else if (this.isVisible(this.validationCodeElement)) {
            if (window.validationService.isFieldValid(this.validationCodeElement)) {
                this.validateAccount();
            }
        }
        else if (this.isVisible(this.setPasswordElement)) {
            if (window.validationService.isFieldValid(this.setPasswordElement)) {
                this.setPassword();
            }
        }
        else if (window.validationService.isFieldValid(this.passwordElement)) {
            await this.verifyLoginAsync();
        }
    }

    private async verifyLoginAsync(): Promise<void> {

        if (!this.isVisible(this.passwordElement) && window.validationService.isFieldValid(this.passwordElement)) {
            // If password field not visible, then make it visible.
            this.setPanelVisibility(SignInPanels.Password);
            return;
        }

        try {
            let postData = {
                email: this.emailElement.value,
                password: this.passwordElement.value,
                returnUrl: window.UrlParser.GetParameterByName("ReturnUrl")
            };

            window.FormSubmitToggle.DisableForm(this.formSelector);

            const response = await fetch(this.localeUrlPrefix + this.verifyLoginUrl, {
                body: JSON.stringify(postData),
                headers: { 'Content-Type': 'application/json' },
                method: "post",
            });

            if (response.ok) {
                const data = await response.json();

                if (!data) { // Server or transport error.
                    await this.logAndShowErrorAsync("Unable to check your login due to an error.", "Unable to log in due to server error.");
                    return;
                }

                if (data.redirectUrl) {
                    // Email / password combo works.
                    window.location.href = data.redirectUrl;
                } else {
                    // Failed to login.
                    await this.logAndShowErrorAsync(data.errorText)
                }
            } else {
                await this.logAndShowErrorAsync("Unable to log you in due to an error.", "Unable to log in.");
            }
        } catch (error) {
            await this.logAndShowErrorAsync("An unexpected error occurred.", "Fetch error: " + error.message);
        }
    }

    private async logAndShowErrorAsync(userMessage: string, techMessage: string = ""): Promise<void> {
        if (techMessage) {
            // Tech message is optional and only logged if there is a real error.
            window.IssueLogger.log(techMessage, this.context);
        }
        this.showValidationError(userMessage, SignInPanels.Password);
        await this.clearPasswordEntryAsync();
        window.FormSubmitToggle.EnableForm(this.formSelector);
    }

    private async clearPasswordEntryAsync(): Promise<void> {
        // Reset the password field validation.
        let form = document.querySelector(this.formSelector) as HTMLFormElement;
        await window.validationService.validateForm(form);

        this.passwordElement.value = "";
        let passwordValidator = document.getElementById("password-main-validation");
        passwordValidator.innerText = "";
        passwordValidator.classList.add("field-validation-valid");
        passwordValidator.classList.remove("field-validation-error");
    }

    private setPassword(): void {

        if (this.isVisible(this.setPasswordElement) && window.validationService.isFieldValid(this.setPasswordElement)) {

            let postData = {
                email: this.emailElement.value,
                newPassword: this.setPasswordElement.value,
                validationCode: this.validationCodeElement.value,
            };

            window.FormSubmitToggle.DisableForm(this.formSelector);
            fetch(this.localeUrlPrefix + this.setPasswordUrl, {
                body: JSON.stringify(postData),
                headers: { 'Content-Type': 'application/json' },
                method: "post",
            }).then((response: Response) => {
                if (response.ok) {
                    return response.json();
                }
                window.IssueLogger.log("Unable to set account active.", this.context);
                this.showValidationError("Unable to set your password due to an error.", SignInPanels.SetPassword);
                window.FormSubmitToggle.EnableForm(this.formSelector);
            }).then((data) => {
                if (!data) { // Server or transport error.
                    window.IssueLogger.log("Unable to set account active due to server error.", this.context);
                    this.showValidationError("Unable to set your account up due to an error.", SignInPanels.SetPassword);
                    window.FormSubmitToggle.EnableForm(this.formSelector);
                    return;
                }

                if (data.success) {
                    window.location.href = data.redirectUrl;
                }
                else {
                    this.showValidationError("Unable to set your password, please ensure it is more than 6 characters.", SignInPanels.SetPassword);
                    window.FormSubmitToggle.EnableForm(this.formSelector);
                }
            });
        }
        else {
            this.setPanelVisibility(SignInPanels.SetPassword);
        }
    }

    private validateAccount(): void {

        if (this.isVisible(this.validationCodeElement) && window.validationService.isFieldValid(this.validationCodeElement)) {

            let postData = {
                email: this.emailElement.value,
                validationCode: this.validationCodeElement.value,
            };

            window.FormSubmitToggle.DisableForm(this.formSelector);
            fetch(this.localeUrlPrefix + "/ws/validate-account", {
                body: JSON.stringify(postData),
                headers: { 'Content-Type': 'application/json' },
                method: "post",
            }).then((response: Response) => {
                if (response.ok) {
                    return response.json();
                }
                window.IssueLogger.log("Unable to validate account.", this.context);
                this.showValidationError("Unable to validate your account due to an error.", SignInPanels.Validation);
                window.FormSubmitToggle.EnableForm(this.formSelector);
            }).then((data) => {
                window.FormSubmitToggle.EnableForm(this.formSelector);
                if (!data) { // Server or transport error.
                    window.IssueLogger.log("Unable to validate account due to server error.", this.context);
                    this.showValidationError("Unable to check your log in due to an error.", SignInPanels.Validation);
                    return;
                }

                if (data.accountValidated) {
                    // Account validated OK, check password.
                    this.setPanelVisibility(SignInPanels.SetPassword);
                }
                else {
                    this.showValidationError("The validation code did not match, please check and try again.", SignInPanels.Validation);
                }
            });
        }
        else {
            // If password field not visible, then make it visible.
            this.setPanelVisibility(SignInPanels.Validation);
        }
    }

    private setPanelVisibility(panelToMakeVisible: SignInPanels): void {
        let panelEmail = document.getElementById("sign-in-email");
        let panelPassword = document.getElementById("sign-in-password");
        let panelSetPassword = document.getElementById("sign-in-set-password");
        let panelValidation = document.getElementById("sign-in-validation");

        panelEmail.classList.add("hidden");
        panelPassword.classList.add("hidden");
        panelSetPassword.classList.add("hidden");
        panelValidation.classList.add("hidden");
        this.backToEmailPanelElement.classList.add("hidden");

        if (panelToMakeVisible == SignInPanels.Email) {
            panelEmail.classList.remove("hidden");
            this.emailElement.focus();
        }
        else if (panelToMakeVisible == SignInPanels.Password) {
            panelPassword.classList.remove("hidden");
            this.backToEmailPanelElement.classList.remove("hidden");
            this.passwordElement.focus();
        }
        else if (panelToMakeVisible == SignInPanels.SetPassword) {
            panelSetPassword.classList.remove("hidden");
            this.backToEmailPanelElement.classList.remove("hidden");
            this.setPasswordElement.focus();
        }
        else {
            panelValidation.classList.remove("hidden");
            this.backToEmailPanelElement.classList.remove("hidden");
            this.validationCodeElement.focus();
        }
    }

    private registerEnterKeyCallback(element: HTMLElement, callback: Function): void {
        element.addEventListener("keyup", (ev) => {
            if (ev.key == "Enter") {
                if (window.FormSubmitToggle.IsFormEnabled(this.formSelector)) {
                    callback();
                }
            }
        });
    }
}
